/**
 * Forma's head: the movement's cover, the title and the line under it, and the view switch. ONE
 * element across the three views, so an arrow key on the switch keeps its focus while the view
 * under it changes.
 */

import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import photo from '~/shared/ui/photoBox.module.css';
import segmented from '~/shared/ui/segmented.module.css';

import styles from './Forma.module.css';

export const FORMA_MODES = ['one', 'all', 'clips'] as const;
export type FormaMode = (typeof FORMA_MODES)[number];

interface FormaHeadProps {
  readonly mode: FormaMode;
  readonly onMode: (mode: FormaMode) => void;
  readonly onPrefetchMatrix: () => void;
  /** The movement's cover box, or `null` for a view about no one movement. */
  readonly cover: { readonly url: string | null } | null;
  readonly title: ReactNode;
  /** The whole name, for a title the one line cuts short. */
  readonly titleHint?: string | undefined;
  /** The hand-over for the title, which the movement list brings. */
  readonly titleReveal: string;
  readonly sub: string;
  /** The hand-over for the line under the title, from the view's own gate. */
  readonly subReveal: string;
}

export function FormaHead({
  mode,
  onMode,
  onPrefetchMatrix,
  cover,
  title,
  titleHint,
  titleReveal,
  sub,
  subReveal,
}: FormaHeadProps) {
  const { t } = useTranslation();
  return (
    <header className={styles.head}>
      {cover === null ? null : (
        <span className={styles.headCover}>
          {cover.url === null ? null : (
            <img
              alt=""
              className={`${String(photo.photo)} ${String(styles.headCoverImg)}`}
              decoding="async"
              src={cover.url}
            />
          )}
        </span>
      )}
      <div className={styles.headText}>
        <h2 className={styles.headTitle} title={titleHint}>
          <span className={titleReveal}>{title}</span>
        </h2>
        <p className={`${String(styles.headSub)} ${subReveal}`}>{sub}</p>
      </div>
      <RadioGroup
        aria-label={t('web:forma.view.label')}
        className={`${String(segmented.seg)} ${String(styles.viewSwitch)}`}
        onValueChange={(next) => {
          const chosen = FORMA_MODES.find((option) => option === next);
          if (chosen !== undefined) onMode(chosen);
        }}
        value={mode}
      >
        {FORMA_MODES.map((option) => (
          <Radio.Root
            className={(radio) =>
              `${String(segmented.segOption)} ${radio.checked ? String(segmented.segOptionOn) : ''}`
            }
            key={option}
            onFocus={option === 'all' ? onPrefetchMatrix : undefined}
            onMouseEnter={option === 'all' ? onPrefetchMatrix : undefined}
            value={option}
          >
            {t(`web:forma.view.${option}`)}
          </Radio.Root>
        ))}
      </RadioGroup>
    </header>
  );
}
